import React, { ReactNode } from "react";
import styled from "styled-components";
import IconButton from "./IconButton";
import Button from "./Button";
import SVG from "src/utils/SVG";
import ModalPortal from "src/modalPortal";
import { CSSTransition } from "react-transition-group";
import { RotatingLines } from "react-loader-spinner";

interface ConfirmModalProps {
  isOpen: boolean;
  title: string;
  children: ReactNode;
  onClose: () => void;
  onConfirm: () => void;
  confirmText?: string;
  cancelText?: string;
  isLoading?: boolean;
}

function ConfirmModal({
  isOpen,
  title,
  children,
  onClose,
  onConfirm,
  confirmText = "확인",
  cancelText = "취소",
  isLoading = false,
}: ConfirmModalProps) {
  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <CSSTransition in={isOpen} classNames="modal" timeout={300} unmountOnExit>
      <ModalPortal>
        <ModalOverlay onClick={handleOverlayClick}>
          <ModalWrapper onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>{title}</ModalTitle>
              <IconButton btnName="팝업닫기" onClick={onClose}>
                <SVG name="modalClose" width={24} height={24} viewBox="0 0 24 24" />
              </IconButton>
            </ModalHeader>
            <ModalBody>{children}</ModalBody>
            <ModalFooter isLoading={isLoading}>
              <Button 
                variant="contained" 
                color="secondary" 
                onClick={isLoading ? undefined : onClose}
                className={isLoading ? "disabled" : ""}
              >
                {cancelText}
              </Button>
              <Button 
                variant="contained" 
                onClick={isLoading ? undefined : onConfirm}
                className={isLoading ? "disabled" : ""}
              >
                {isLoading ? (
                  <RotatingLines width="16" strokeColor="#ffffff" strokeWidth="2" />
                ) : (
                  confirmText
                )}
              </Button>
            </ModalFooter>
          </ModalWrapper>
        </ModalOverlay>
      </ModalPortal>
    </CSSTransition>
  );
}

const ModalOverlay = styled.div`
  z-index: 2000;
  position: fixed;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 15px;
  background-color: rgba(60, 60, 60, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ModalWrapper = styled.div`
  position: relative;
  overflow: hidden;
  width: 100%;
  max-width: 400px;
  background-color: #fff;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
`;

const ModalHeader = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 20px 10px;
  border-bottom: 1px solid ${({ theme }) => theme.color.linePrimary};
`;

const ModalTitle = styled.h3`
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: ${({ theme }) => theme.color.primary};
`;

const ModalBody = styled.div`
  padding: 20px;
`;

const ModalFooter = styled.div<{ isLoading?: boolean }>`
  display: flex;
  gap: 10px;
  padding: 20px;
  border-top: 1px solid ${({ theme }) => theme.color.linePrimary};

  > button {
    flex: 1;
    
    &.disabled {
      opacity: 0.6;
      cursor: not-allowed;
      pointer-events: ${({ isLoading }) => (isLoading ? "none" : "auto")};
    }
  }
`;

export default ConfirmModal;